/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import DatePicker from 'react-datepicker'

import {
  ExpandableOverviewItem,
  Table as Accounts,
  Table as Consolidations,
  Spinner,
  SvgIcon,
  ChooseTableRow,
  Button,
  Notification,
  CurrencyInput,
  ConsolidationBankAccount,
  NavLink,
  Warning
} from '../'

import {
  showAsCurrency,
  encodeBase64,
  decodeBase64,
  roundToDecimals,
  getTotal
} from '../../utils'

import { ReadOnlyContext } from '../../context'

import { CONTENTTYPE_CONSOLIDATION } from '../../constants'

const MUTATION_CONSOLIDATION = gql`
  mutation updateConsolidation($input: ConsolidationMutationInput!) {
    consolidationMutate(input: $input) {
      consolidation {
        id
        bankaccountRef
        amount
        statementExpiresAt
        loanapplication {
          id
        }
      }
    }
  }
`

const QUERY_FILERELATIONS = gql`
  query($contentType: ID!, $consolidation: UUID!) {
    filerelations(contentType_Id: $contentType, objectId: $consolidation, orderBy: "-created_at", first: 1) {
      edges {
        node {
          id
          file {
            id
            asset
          }
        }
      }
    }
  }
`

const QUERY_WORKFLOWRUNS = gql`
  query($loanApplication: String!) {
    workflowruns(description_Icontains: $loanApplication, orderBy: "-created_at") {
      edges {
        node {
          id
          description
        }
      }
    }
  }
`

const MUTATION_FILERELATION = gql`
  mutation createFileRelation($input: FileRelationMutationInput!) {
    filerelationMutate(input: $input) {
      filerelation {
        id
        file {
          id
          asset
        }
      }
    }
  }
`


export default class LoanApplicationCreditReport extends React.Component {
  static propTypes = {
    creditReport: PropTypes.shape({
      id: PropTypes.string,
      createdAt: PropTypes.string
    }).isRequired,
    loanApplication: PropTypes.string.isRequired,
    accounts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      creditreportsupplier: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
      }),
      balance: PropTypes.number,
      paymentFreqDesc: PropTypes.string,
      instalment: PropTypes.number,
      overdue: PropTypes.number,
      consolidation: PropTypes.arrayOf(PropTypes.object),
    })).isRequired,
    consolidations:  PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      amount: PropTypes.number,
      statementExpiresAt: PropTypes.string,
      bankaccountRef: PropTypes.string,
      approvedBy: PropTypes.shape({
        id: PropTypes.string
      }),
      creditreportaccount: PropTypes.shape({
        id: PropTypes.string,
        creditreportsupplier: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string
        }),
        balance: PropTypes.number
      })
    })),
    open: PropTypes.bool.isRequired,
    selectedFile: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    accountSelectedToConsolidate: null,
    editConsolidation: null,
    linkedConsolidationFiles: {}
  }

  selectAccountForConsolidation = (account) => this.setState({ accountSelectedToConsolidate: account })
  clearAccountSelection = () => this.setState({ accountSelectedToConsolidate: null })

  consolidateSelectedAccount = (consolidationMutate, creditReportAccount) => {
    consolidationMutate({
      variables: {
        input: {
          loanapplication: this.props.loanApplication,
          creditreportaccount: creditReportAccount
        }
      }
    })
    .then(() => this.setState({ accountSelectedToConsolidate: null }, this.props.refetchData))
    .catch(error => this.setState({ accountSelectedToConsolidate: null }))
  }

  updateConsolidation = (consolidationMutate, consolidation, amountToConsolidate, statementExpiresAt) => {
    let input = {
      id: consolidation
    }
    if (amountToConsolidate) {
      input.amount = amountToConsolidate
    }
    if (statementExpiresAt) {
      input.statementExpiresAt = statementExpiresAt
    }
    consolidationMutate({ variables: { input } })
    .then(() => this.setState({ editConsolidation: null }))
    .catch(error => this.setState({ editConsolidation: null }))
  }

  resetEdit = () => {
    this.setState({ editConsolidation: null })
  }

  render() {
    let { creditReport, accounts, consolidations, open, onClick, className } = this.props
    let accountsWithOutstandingDebt = accounts ? accounts.filter(account => account.balance > 0) : []
    let accountsNotConsolidatedInFull = accountsWithOutstandingDebt.filter(account => (
      account.consolidation.edges.length === 0 ||
      account.consolidation.edges[0].node.amount < account.openBalance  // NOTE: ran into a case where consolidation amount was larger than account balance
    ))
    let accountsBalanceTotal = accountsNotConsolidatedInFull.length > 0
      ? showAsCurrency('R', roundToDecimals(getTotal(accountsNotConsolidatedInFull.map(account => account.balance)), 2))
      : null
    let consolidationsAmountTotal = consolidations.length > 0
      ? showAsCurrency('R', roundToDecimals(getTotal(consolidations.map(consolidation => consolidation.amount)), 2))
      : null

    return (
      <ExpandableOverviewItem
        title='Credit report'
        summary={accounts.length > 0 && accountsBalanceTotal
          ? `Accounts balance: ${accountsBalanceTotal}${consolidationsAmountTotal ? `, Consolidations total: ${consolidationsAmountTotal}`: ''}`
          : creditReport
            ? 'No outstanding debt'
            : 'No credit report received yet'
        }
        // iconSymbol={identityAllocated ? 'check' : null}
        // iconColour={identityAllocated ? 'success' : null}
        open={open}
        onClick={onClick}
        className={className}>
        {!creditReport
          ? <div className='slds-grid slds-m-left_small slds-m-bottom_large'>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='light'
                size='small'
                className='slds-m-right_small'
              />
              No credit report received.
              <Query query={QUERY_WORKFLOWRUNS} variables={{ loanApplication: decodeBase64(this.props.loanApplication) }}>
                {({ loading, error, data }) => {
                  if (loading || error) {
                    return null
                  }
                  if (data.workflowruns.edges.length === 0) {
                    return '(No workflow runs found)'
                  } else {
                    return (
                      <div className='slds-m-left_small'>
                        See the corresponding <NavLink to={`/os/operations/general/workflows/${data.workflowruns.edges[0].node.id}`}>workflow run</NavLink>.
                      </div>
                    )
                  }
                }}
              </Query>
            </div>
          : accountsWithOutstandingDebt.length === 0
            ? <Notification
                type='info'
                message={`Client has no outstanding debt.  Credit report as of ${moment(creditReport.createdAt).format('llll')}`}
                closeIcon={false}
              />
            : <React.Fragment>
                <div>
                  <div className='u-font-size-larger u-font-weight-bold'>
                    Accounts
                  </div>
                  {accountsNotConsolidatedInFull.length > 0
                    ? <ReadOnlyContext.Consumer>
                        {readOnly => (
                          <React.Fragment>
                            {!readOnly &&
                              <div className='slds-text-color_weak slds-m-bottom_small'>
                                Choose account to consolidate
                              </div>
                            }
                            <ChooseTableRow>
                              <Accounts
                                columns={[
                                  'Supplier',
                                  'Balance',
                                  'Instalment',
                                  'History'
                                ]}
                                rows={accountsNotConsolidatedInFull.map(account => (
                                  <tr onClick={() => readOnly ? false : this.selectAccountForConsolidation(account)} key={`account_${account.id}`}>
                                    <td>{account.creditreportsupplier && account.creditreportsupplier.name}</td>
                                    <td>{showAsCurrency('R', account.balance)}</td>
                                    <td>{showAsCurrency('R', `${account.instalment} ${account.paymentFreqDesc ? `(${account.paymentFreqDesc.toLowerCase()})` : ''}`)}</td>
                                    <td title={account.paymentHistory}>
                                      {account.paymentHistory ? account.paymentHistory.slice(-5) : ''}
                                    </td>
                                  </tr>
                                ))}
                                objectRows={true}
                                cellPadding={true}
                                className='slds-m-bottom_medium'
                              />
                            </ChooseTableRow>
                          </React.Fragment>
                        )}
                      </ReadOnlyContext.Consumer>
                    : <div className='slds-text-color_weak slds-m-bottom_small slds-grid'>
                        <SvgIcon
                          variant='utility'
                          symbol='info_alt'
                          colour='light'
                          size='x-small'
                          className='slds-m-right_x-small'
                        />
                        No accounts not already selected for consolidation.
                      </div>
                  }
                  {this.state.accountSelectedToConsolidate &&
                    <div className='slds-m-bottom_medium slds-grid'>
                      <div>
                        <SvgIcon
                          variant='utility'
                          symbol='info_alt'
                          colour='warning'
                          size='x-small'
                          className='slds-m-right_x-small'
                        />
                        Consolidate the selected account?
                      </div>
                      <div className='slds-m-left_medium'>
                        <Button
                          onClick={this.clearAccountSelection}
                          minWidth={90}
                          className='slds-m-right_x-small'>
                          No
                        </Button>
                        <Mutation mutation={MUTATION_CONSOLIDATION}>
                          {(consolidationMutate, { data }) => (
                            <Button
                              type={data && data.error ? 'destructive' : 'brand'}
                              minWidth={90}
                              disabled={data}
                              onClick={() => this.consolidateSelectedAccount(consolidationMutate, this.state.accountSelectedToConsolidate.id)}>
                              Yes
                            </Button>
                          )}
                        </Mutation>
                      </div>
                    </div>
                  }
                </div>
                <div className='slds-m-bottom_large'>
                  <div className='u-font-size-larger u-font-weight-bold slds-m-bottom_small'>
                    Consolidations
                  </div>
                  {consolidations.length === 0
                    ? <div className='slds-grid slds-m-left_small slds-m-bottom_large'>
                        <SvgIcon
                          variant='utility'
                          symbol='info'
                          colour='light'
                          size='small'
                          className='slds-m-right_small'
                        />
                        No accounts consolidated.
                      </div>
                    : <ChooseTableRow>
                        <ReadOnlyContext.Consumer>
                          {readOnly => (
                            <Consolidations
                              columns={[
                                'Account',
                                'Amount',
                                'Statement',
                                'Expiry',
                                // ''
                              ]}
                              rows={this.props.consolidations.map(consolidation => (
                                <React.Fragment key={`consolidation_${consolidation.id}`}>
                                  <tr onClick={() => readOnly ? false : this.setState({ editConsolidation: consolidation.id })}>
                                    <td>{consolidation.creditreportaccount.creditreportsupplier && consolidation.creditreportaccount.creditreportsupplier.name}</td>
                                    <td>{showAsCurrency('R', consolidation.amount)}</td>
                                    <td>
                                      <StatementFile
                                        file={this.state.linkedConsolidationFiles[consolidation.id]}
                                        consolidation={consolidation.id}
                                        viewFile={file => this.props.changeSelectedFile(file)}
                                      />
                                    </td>
                                    <td>
                                      {consolidation.statementExpiresAt
                                        ? moment(consolidation.statementExpiresAt).format('ll')
                                        : 'Not specified'
                                      }
                                    </td>
                                  </tr>
                                  {this.state.editConsolidation === consolidation.id &&
                                    <ConsolidationEdit
                                      consolidation={consolidation.id}
                                      accountBalance={consolidation.creditreportaccount.balance}
                                      amount={consolidation.amount}
                                      statementExpiresAt={consolidation.statementExpiresAt}
                                      selectedStatementFile={this.props.selectedFile}
                                      bankAccountRef={consolidation.bankaccountRef}
                                      onCancelClick={this.resetEdit}
                                      onSaved={this.resetEdit}
                                      linkedConsolidationFiles={this.state.linkedConsolidationFiles}
                                      updateLinkedConsolidationFiles={(linkedConsolidationFiles) => (
                                        this.setState({ linkedConsolidationFiles })
                                      )}
                                    />
                                  }
                                </React.Fragment>
                              ))}
                              objectRows={true}
                              cellPadding={true}
                              className='slds-m-bottom_large'
                            />
                          )}
                        </ReadOnlyContext.Consumer>
                      </ChooseTableRow>
                  }
                </div>
              </React.Fragment>
        }
      </ExpandableOverviewItem>
    )
  }
}

class ConsolidationEdit extends React.PureComponent {
  static propTypes = {
    consolidation: PropTypes.string.isRequired,
    accountBalance: PropTypes.number.isRequired,
    amount: PropTypes.number,
    statementExpiresAt: PropTypes.string,
    selectedStatementFile: PropTypes.string,
    linkedConsolidationFiles: PropTypes.array.isRequired,
    updateLinkedConsolidationFiles: PropTypes.func.isRequired
  }

  state = {
    amount: this.props.amount,
    statementExpiresAt: this.props.statementExpiresAt ? moment(this.props.statementExpiresAt) : null
  }

  valuesChanged = () => (
    this.state.amount !== this.props.amount ||  // NOTE: double check string vs int values for 'amount'
    this.hasStatementExpiredAtChanged()
  )

  hasStatementExpiredAtChanged = () => (
    this.state.statementExpiresAt && !this.state.statementExpiresAt.isSame(moment(this.props.statementExpiresAt))
  )

  updateConsolidation = (consolidationMutate) => {
    let input = {
      id: this.props.consolidation
    }
    if (this.state.amount !== this.props.amount) {
      input.amount = this.state.amount
    }
    if (this.hasStatementExpiredAtChanged()) {
      input.statementExpiresAt = moment(this.state.statementExpiresAt).format('YYYY-MM-DD')
    }
    consolidationMutate({ variables: { input } })
    .then(() => this.props.onSaved())
    .catch(error => this.props.onSaved())
  }

  linkStatementFile = (filerelationMutate) => {
    let input = {
      file: this.props.selectedStatementFile,
      contentType: CONTENTTYPE_CONSOLIDATION,
      objectId: decodeBase64(this.props.consolidation)
    }
    filerelationMutate({ variables: { input } })
      .then(({ data }) => {
        let linkedConsolidationFiles = Object.assign({}, this.props.linkedConsolidationFiles)
        linkedConsolidationFiles[this.props.consolidation] = data.filerelationMutate.filerelation.file.id
        this.props.updateLinkedConsolidationFiles(linkedConsolidationFiles)
      })
  }

  render() {
    return (
      <tr>
        <td className='consolidation-edit' colSpan={4}>
          <div className='slds-m-left_large'>
            <div className='slds-grid'>
              <div className='slds-size_1-of-3'>
                Account balance:
              </div>
              <div className='slds-size_1-of-3 slds-text-color_weak'>
                {showAsCurrency('R', this.props.accountBalance)}
              </div>
            </div>
            <div className='slds-grid slds-m-bottom_large'>
              <div className='slds-size_1-of-3'>
                Amount to consolidate:
              </div>
              <CurrencyInput
                value={this.state.amount}
                onChange={e => this.setState({ amount: e.target.value })}
              />
            </div>
            <div className='slds-grid'>
              <div className='slds-size_1-of-3'>
                Statement file:
              </div>
              <div className='slds-size_1-of-3'>
                <Mutation mutation={MUTATION_FILERELATION}>
                  {(filerelationMutate, { data: frData }) => (
                    <Query
                      query={QUERY_FILERELATIONS}
                      variables={{
                        contentType: encodeBase64(CONTENTTYPE_CONSOLIDATION, "ContentTypeNode"),
                        consolidation: decodeBase64(this.props.consolidation)
                      }}>
                      {({ loading, error, data }) => {
                        if (loading || error) {
                          return null
                        }
                        if (data.filerelations.edges.length > 0 || (frData && frData.filerelationMutate.filerelation)) {
                          return 'Linked'
                        } else {
                          return (
                            <Button
                              disabled={!this.props.selectedStatementFile}
                              onClick={() => this.linkStatementFile(filerelationMutate)}>
                              Link selected
                            </Button>
                          )
                        }
                      }}
                    </Query>
                  )}
                </Mutation>
              </div>
            </div>
            <div className='slds-grid slds-m-bottom_large'>
              <div className='slds-size_1-of-3'>
                Statement expiry:
              </div>
              <DatePicker
                selected={this.state.statementExpiresAt}
                onChange={statementExpiresAt => this.setState({ statementExpiresAt })}
                dateFormat='D MMM YYYY'
                minDate={moment()}
                maxDate={moment().add(90, 'days')}
              />
            </div>
            <div>
              <div className='slds-size_1-of-3 u-font-weight-bold'>
                Bank details:
              </div>
              <ConsolidationBankAccount
                consolidationId={this.props.consolidation}
                bankAccountRef={this.props.bankAccountRef}
                tableRowView={true}
              />
            </div>
            <div className='slds-m-top_large slds-align_absolute-center'>
              <Button
                type='brand'
                minWidth={110}
                onClick={this.props.onCancelClick}>
                Cancel
              </Button>
              <Mutation mutation={MUTATION_CONSOLIDATION}>
                {(consolidationMutate, { data }) => (
                  <Button
                    type='brand'
                    minWidth={110}
                    disabled={!this.valuesChanged()}
                    onClick={() => this.updateConsolidation(consolidationMutate)}>
                    Save
                  </Button>
                )}
              </Mutation>
            </div>
          </div>
        </td>
      </tr>
    )
  }
}

class StatementFile extends React.PureComponent {
  render() {
    if (this.props.file) {
      return (
        <Button type='reset' onClick={e => {
          e.stopPropagation()
          this.props.viewFile(this.props.file)
        }}>
          View file
        </Button>
      )
    } else {
      return (
        <Query
          query={QUERY_FILERELATIONS}
          variables={{
            contentType: CONTENTTYPE_CONSOLIDATION,
            consolidation: decodeBase64(this.props.consolidation)
          }}>
          {({ loading, error, data }) => {
            if (loading) {
              return <Spinner className='slds-is-relative' size='small' />
            }
            if (error) {
              return (
                <Warning inline={true} iconSize='x-small'>
                  Loading file failed.
                </Warning>
              )
            }
            if (data.filerelations.edges.length === 0) {
              return 'No statement linked'
            } else {
              return (
                <Button type='reset' onClick={e => {
                  e.stopPropagation()
                  this.props.viewFile(data.filerelations.edges[0].node.file.id)
                }}>
                  View file
                </Button>
              )
            }
          }}
        </Query>
      )
    }
  }
}

StatementFile.propTypes = {
  consolidation: PropTypes.string,
  file: PropTypes.string,
  viewFile: PropTypes.func.isRequired
}



// WEBPACK FOOTER //
// ./src/components/loanapplication-creditreport/index.js